import {
  Box,
  Button,
  Center,
  Flex,
  IconButton,
  ListItem,
  Spinner,
  Stack,
  Text,
  UnorderedList,
} from '@chakra-ui/react';
import type {
  ProductSuggestionsData,
  VoucherEligibilityItem,
  VoucherGetByCodeResponse,
  VoucherRewardMessage,
  VoucherTermMessage,
  VoucherWarningMessage,
} from '@diamond/shared/types';
import {
  ArrowBack as ArrowBackIcon,
  Close as CloseIcon,
  ConfirmationNumberOutlined as VoucherIcon,
  WarningAmberOutlined,
} from '@mui/icons-material';
import Image from 'next/image';

import { VoucherRelatedProducts } from '../../voucher';

type VoucherDetailsItem = VoucherGetByCodeResponse | VoucherEligibilityItem;

interface VoucherDetailsContentProps {
  voucher: VoucherDetailsItem;
  isApplied: boolean;
  isProcessing: boolean;
  isEligible?: boolean;
  isLoadingDetails?: boolean;
  detailFetchError?: string | null;
  onApply: () => void;
  onBack: () => void;
  onClose: () => void;
  onRetry?: () => void;
  isDisabled?: boolean;
  disabledReason?: string | null;
}

const getVoucherDescription = (voucher: VoucherGetByCodeResponse): string => {
  return voucher.desc || '';
};

const getRewardMessages = (
  voucher: VoucherGetByCodeResponse
): VoucherRewardMessage[] => {
  return voucher.reward_messages || [];
};

const getTermMessages = (
  voucher: VoucherGetByCodeResponse
): VoucherTermMessage[] => {
  return voucher.term_messages || [];
};

const getWarningMessages = (
  voucher: VoucherGetByCodeResponse
): VoucherWarningMessage[] => {
  return voucher.warning_messages || [];
};

const getRuleProducts = (
  voucher: VoucherGetByCodeResponse
): ProductSuggestionsData => {
  return voucher.rule_products || [];
};

export const VoucherDetailsContent = ({
  voucher,
  isApplied,
  isProcessing,
  isEligible = true,
  isLoadingDetails = false,
  detailFetchError = null,
  onApply,
  onBack,
  onClose,
  onRetry,
  isDisabled = false,
  disabledReason = null,
}: VoucherDetailsContentProps) => {
  const bannerUrl = voucher.banner_url || '/images/voucher.png';
  const description =
    'desc' in voucher
      ? getVoucherDescription(voucher as VoucherGetByCodeResponse)
      : '';
  const rewardMessages =
    'reward_messages' in voucher
      ? getRewardMessages(voucher as VoucherGetByCodeResponse)
      : [];
  const termMessages =
    'term_messages' in voucher
      ? getTermMessages(voucher as VoucherGetByCodeResponse)
      : [];
  const warningMessages =
    'warning_messages' in voucher
      ? getWarningMessages(voucher as VoucherGetByCodeResponse)
      : [];
  const ruleProducts =
    'rule_products' in voucher
      ? getRuleProducts(voucher as VoucherGetByCodeResponse)
      : [];
  return (
    <Flex flexDirection="column" h="100%" maxH="calc(100vh - 120px)">
      {/* Header */}
      <Flex bg="#00003D" flexDirection="column" py="4" px="4" gap="2">
        <Flex justify="space-between" align="center">
          <IconButton
            onClick={onBack}
            colorScheme="white"
            aria-label="Kembali"
            variant="link"
            color="white"
            icon={<ArrowBackIcon />}
            minW="auto"
            h="auto"
            p="1"
            mr="2"
          />
          <Text color="white" fontSize="20px" fontWeight="600" flex="1">
            Voucher
          </Text>
          <IconButton
            onClick={onClose}
            colorScheme="white"
            aria-label="Tutup"
            variant="link"
            color="white"
            icon={<CloseIcon />}
            minW="auto"
            h="auto"
            p="1"
          />
        </Flex>
      </Flex>

      {/* Scrollable Content Area */}
      <Box flex="1" overflowY="auto" bg="white" pb="30px" position="relative">
        {isLoadingDetails && (
          <Center position="absolute" inset={0} bg="white" zIndex={10}>
            <Spinner size="lg" color="#007AFF" />
          </Center>
        )}

        {detailFetchError && !isLoadingDetails && (
          <Center py={8} px={4} flexDirection="column">
            <Stack spacing={4} textAlign="center" width="100%">
              <Text fontSize="lg" fontWeight="bold" color="red.500">
                Gagal memuat detail voucher
              </Text>
              <Text fontSize="sm" color="gray.600">
                {detailFetchError}
              </Text>
              <Button
                variant="solid"
                colorScheme="blue"
                onClick={onRetry}
                size="sm"
                maxW="200px"
                mx="auto"
              >
                Coba Lagi
              </Button>
            </Stack>
          </Center>
        )}

        <Box px="16px" py="20px" opacity={isLoadingDetails ? 0.5 : 1}>
          <Box
            position="relative"
            w="100%"
            h="174px"
            overflow="hidden"
            mb="20px"
            borderRadius="8px"
          >
            <Image
              src={bannerUrl}
              width={488}
              height={174}
              alt={voucher.title}
              style={{
                borderRadius: 8,
                objectFit: 'cover',
                width: '100%',
                height: '100%',
              }}
            />
          </Box>

          <Stack spacing="20px" mb="24px">
            <Flex gap="8px" flexDirection="column">
              <Text
                fontSize="16px"
                fontWeight="bold"
                color="#000000"
                mb="4px"
                textTransform="capitalize"
              >
                {voucher.title}
              </Text>
              {description && (
                <Text fontSize="14px" color="#000000">
                  {description}
                </Text>
              )}
            </Flex>

            {rewardMessages.length > 0 && (
              <Flex flexDirection="column">
                {rewardMessages.map((reward, idx) => (
                  <Box
                    key={idx}
                    bg="#F2F2F7"
                    p="8px"
                    borderRadius="8px"
                    mb="12px"
                  >
                    <Stack spacing="12px">
                      <Flex align="flex-start" gap="4px" flexWrap="wrap">
                        <Text
                          fontSize="14px"
                          fontWeight="medium"
                          color="#000000"
                          flexShrink={0}
                        >
                          Reward
                        </Text>
                        <Text
                          color="#FF0000"
                          fontSize="14px"
                          fontWeight="medium"
                          flexShrink={0}
                        >
                          •
                        </Text>
                        <Text
                          color="#FF0000"
                          fontSize="14px"
                          fontWeight="medium"
                          wordBreak="break-word"
                        >
                          {reward.messages}
                        </Text>
                      </Flex>
                      <Stack spacing="4px">
                        {reward.products.map((product, productIdx) => (
                          <Text
                            key={productIdx}
                            fontSize="14px"
                            color="#000000"
                            textTransform="capitalize"
                          >
                            Pembelian {product.product_name}
                          </Text>
                        ))}
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Flex>
            )}

            {termMessages.length > 0 && (
              <Box>
                <Text
                  fontSize={{ base: 'md', md: '14px' }}
                  fontWeight="medium"
                  color="#000000"
                  mb="12px"
                >
                  Syarat dan ketentuan pemakaian voucher
                </Text>
                <UnorderedList spacing="8px">
                  {termMessages.map((term, idx) => (
                    <ListItem key={idx} fontSize="14px" color="#000000">
                      <Text
                        as="span"
                        dangerouslySetInnerHTML={{ __html: term.messages }}
                      />

                      {term.products.length > 0 && (
                        <UnorderedList mt="4px" ml="4" spacing="4px">
                          {term.products.map((product, productIdx) => (
                            <ListItem
                              key={productIdx}
                              fontSize="14px"
                              color="#000000"
                            >
                              <Text as="span" textTransform="capitalize">
                                {product.product_name}
                              </Text>
                            </ListItem>
                          ))}
                        </UnorderedList>
                      )}
                    </ListItem>
                  ))}
                </UnorderedList>
              </Box>
            )}

            {warningMessages.length > 0 && (
              <Box>
                {warningMessages.map((warning, idx) => (
                  <Flex key={idx} align="flex-start" gap="8px">
                    <WarningAmberOutlined
                      style={{
                        width: '16px',
                        height: '16px',
                        color: '#FF8D28',
                        marginTop: '2px',
                        flexShrink: 0,
                      }}
                    />
                    <Text fontSize="12px" color="#FF8D28" fontWeight="medium">
                      {warning.messages}
                    </Text>
                  </Flex>
                ))}
              </Box>
            )}
          </Stack>

          {/* Produk Terkait Section */}
          {ruleProducts.length > 0 && (
            <Box mt="20px">
              <VoucherRelatedProducts
                title="Produk Terkait"
                data={ruleProducts}
                maxProducts={2}
                section="Voucher Related Products"
                pageName="Voucher Details"
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Sticky Footer */}
      <Flex
        position="sticky"
        bottom={0}
        justify="space-between"
        align="center"
        px="16px"
        py="12px"
        borderTop="1px solid #E5E5EA"
        bg="white"
        zIndex={10}
        boxShadow="0 -2px 4px rgba(0, 0, 0, 0.05)"
      >
        <Flex direction="column">
          <Text color="#8E8E93" fontSize="12px" fontWeight="bold">
            Kode Voucher
          </Text>
          <Box display="flex" alignItems="center" gap="4px">
            <VoucherIcon
              style={{ width: '20px', height: '20px', color: '#0000A3' }}
            />
            <Text color="#007AFF" fontSize="20px" fontWeight="semibold">
              {voucher.code}
            </Text>
          </Box>
        </Flex>
        <Flex gap="8px">
          {isApplied ? (
            <Button
              size="md"
              minW="auto"
              px="11px"
              py="12px"
              border="none"
              bg="transparent"
              borderRadius="8px"
              cursor={isProcessing ? 'not-allowed' : 'pointer'}
              color="#0000A3"
              fontSize="14px"
              fontWeight="medium"
              disabled={isProcessing}
              opacity={isProcessing ? 0.6 : 1}
              _hover={
                isProcessing
                  ? {}
                  : { bg: 'transparent', color: '#0000A3', opacity: 0.6 }
              }
              onClick={(e) => {
                e.stopPropagation();
                onApply();
              }}
            >
              <CloseIcon
                style={{ color: 'red', fontSize: '16px', marginRight: '4px' }}
              />
              Batalkan Voucher
            </Button>
          ) : (
            <Button
              size="md"
              variant="solid"
              minW="auto"
              px="11px"
              py="12px"
              cursor={
                isProcessing || !isEligible || isDisabled
                  ? 'not-allowed'
                  : 'pointer'
              }
              onClick={(e) => {
                e.stopPropagation();
                if (isEligible && !isProcessing && !isDisabled) {
                  onApply();
                }
              }}
              disabled={isProcessing || !isEligible || isDisabled}
              opacity={isProcessing || !isEligible || isDisabled ? 0.6 : 1}
            >
              Pakai Voucher
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default VoucherDetailsContent;
